import type { Entity, Relation } from '@/types';

export interface RelationOverride {
  speciesId: string;
  relation: Relation;
  when?: (predator: Entity, prey: Entity) => boolean;
}

export interface RelationResolverOptions {
  overrides?: RelationOverride[];
  preyThreshold?: number;
  predatorThreshold?: number;
}

export class RelationResolver {
  private overrides: RelationOverride[];
  private readonly preyThreshold: number;
  private readonly predatorThreshold: number;

  constructor(options: RelationResolverOptions = {}) {
    this.overrides = options.overrides ?? [];
    this.preyThreshold = options.preyThreshold ?? 1.2;
    this.predatorThreshold = options.predatorThreshold ?? 0.8;
  }

  resolve(a: Entity, b: Entity): Relation {
    if (b.isScriptedNeutral) return 'NEUTRAL';

    for (const override of this.overrides) {
      if (b.speciesId !== override.speciesId) continue;
      if (override.when && !override.when(a, b)) continue;
      return override.relation;
    }

    if (b.mass <= 0) return 'PREY';

    // Non-living things are always consumable regardless of size. A
    // carcass much larger than the blob takes ages to finish and yields
    // diminishing returns (handled in ConsumptionController), but the
    // blob can always *start* eating.
    if (b.isStatic) return 'PREY';

    const ratio = a.mass / b.mass;
    if (ratio >= this.preyThreshold) return 'PREY';
    if (ratio <= this.predatorThreshold) return 'PREDATOR';
    return 'NEUTRAL';
  }

  addOverride(override: RelationOverride): void {
    this.overrides.push(override);
  }
}
